import { useFeatureFlagEnabled } from "posthog-js/react";
import type React from "react";
import { useCallback, useState } from "react";
import { SettingItem } from "../components/SettingItem";
import { apiClient } from "../lib/apiClient";
interface Setting {
	id: string;
	name: string;
	description?: string;
	enabled: boolean;
	requiresSetup: boolean;
	setupMessage?: string;
	setupUrl?: string;
	alwaysEnabled?: boolean;
	isConfigured?: () => Promise<{ isConfigured: boolean; text: React.ReactNode }>;
}
export const SettingsPage: React.FC = () => {
	const isPaypalEnabled = useFeatureFlagEnabled("paypal");
	const isCurrencyEnabled = useFeatureFlagEnabled("currency");

	const [settings, setSettings] = useState<Setting[]>([
		{
			id: "paypal",
			name: "Paypal",
			description: "Turn on to accept payments via Paypal",
			enabled: true,
			requiresSetup: true,
			setupUrl: "/settings/paypal",
		},
		{
			id: "currency",
			name: "Currency",
			description: "Set the currency for your payments",
			enabled: true,
			requiresSetup: true,
			setupMessage: "Set the currency for your payments, default is GBP",
			setupUrl: "/settings/currency",
			alwaysEnabled: true,
			isConfigured: async () => {
				return Promise.resolve({
					isConfigured: false,
					text: "Currency is set to GBP",
				});
			},
		},
		{
			id: "termsandconditions",
			name: "Terms and Conditions",
			enabled: true,
			requiresSetup: true,
			setupUrl: "/settings/termsandconditions",
			alwaysEnabled: true,
			isConfigured: async () => {
				const res = await apiClient().get<{ url: string } | null>(
					"/term/latest/terms_and_conditions",
				);
				const url = res.data?.url ?? null;
				return {
					isConfigured: url !== null,
					text: url ? (
						<a href={url} target="_blank" rel="noreferrer" className="underline">
							View Terms and Conditions
						</a>
					) : (
						"No terms document uploaded yet"
					),
				};
			},
		},
	]);

	// Derived every render so flag values are always current (flags resolve async after mount)
	const visibleSettings = settings.filter((setting) => {
		if (setting.id === "paypal") return isPaypalEnabled === true;
		if (setting.id === "currency") return isCurrencyEnabled === true;
		return true;
	});
	const handleToggle = useCallback((id: string) => {
		setSettings((prev) =>
			prev.map((setting) =>
				setting.id === id && !setting.alwaysEnabled
					? { ...setting, enabled: !setting.enabled }
					: setting,
			),
		);
	}, []);
	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<div className="max-w-2xl mx-auto sm:p-6 pt-6">
				<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
					<div className="px-6 py-5 border-b border-gray-200">
						<h2 className="text-2xl font-bold text-gray-900">Settings</h2>
						<p className="mt-1 text-sm text-gray-500">
							Manage your preferences and account settings
						</p>
					</div>
					<div className="px-6 py-2 divide-y divide-gray-200">
						{visibleSettings.map((setting) => (
							<SettingItem key={setting.id} {...setting} onToggle={handleToggle} />
						))}
					</div>
				</div>
			</div>
		</div>
	);
};
